import React, { useState, MouseEvent, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  addContactsToCartAsync,
  addFrameToCartAsync,
  deleteItemAsync,
  editSavedItemsGlassesWarrantyAsync,
  selectConfig,
  selectFormattedOrderItems,
  selectFormattedOrderItemsStatus,
} from '../../store/wishlist/wishlistSlice';
import {
  atLeastOneEqual,
  convertNumber,
  getBadge,
  getContactDetail,
  getLabelFallback,
  getUrl,
  labels,
} from '@utils/utils';
import { URL_EDIT_WISHLIST_CL } from '@utils/const';
import Image from '@components/Image';
import ButtonLink from '@components/ButtonLink';
import WishlistFrameItemContent from './WishlistFrameItemContent';
import WishlistContactItemContent from './WishlistContactItemContent';
import TrashIcon from '@images/trash.svg';
import {
  WishlistCatentry,
  WishlistItem as WishlistItemInterface,
} from '@store/wishlist/wishlistInterfaces';
import clsx from 'clsx';
import LoaderElement from '@components/skeletons/LoaderElement';
import Loader from '@components/Loader';
import productViewMock from './../../moks/data_product_view_by_id.json';
import services from '../../store/wishlist/wishlistServices';
import { parse } from 'dotenv';

export default function WishlistItem({ item }: { item: WishlistItemInterface }): JSX.Element {
  const dispatch: any = useDispatch();

  const containerRef = useRef();
  const config = useSelector(selectConfig);
  const formattedOrderItems = useSelector(selectFormattedOrderItems);
  const contactDetailsStatus = useSelector(selectFormattedOrderItemsStatus);

  const formattedItem = formattedOrderItems[item.idList];
  const isContact = item.catentries[0].catEntryType?.includes('CON');
  const isContactLoading = isContact && contactDetailsStatus === 'LOADING';
  const isInStock = item.catentries.find(entry =>
    atLeastOneEqual(entry.catEntryType, ['SUN', 'EYE', 'RCON', 'LCON'])
  )?.available;
  const frame = item.catentries.find((item: WishlistCatentry) =>
    atLeastOneEqual(item.catEntryType, ['SUN', 'EYE'])
  );
  const lensAttributesJsonString = item.catentries
                        .find((entry: WishlistCatentry) => entry.catEntryType === 'LENS')?.attributes
                        .find((attr: Attr) => attr.name === "rxc_obj").value as string;
  const lensAttributesList = lensAttributesJsonString && JSON.parse(lensAttributesJsonString)
  const isFrameOnly = !item.catentries.find(
    (item: WishlistCatentry) => item.catEntryType === 'LENS'
  );

  const [itemWidth, setItemWidth] = useState(660);
  const [isWarrantyActive, setWarrantyActive] = useState(false);
  const [badge, setBadge] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setItemWidth((containerRef.current as HTMLElement | undefined)?.clientWidth ?? 0);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    if (!!frame && config.brand === 'TO') { //badge only for TO
      if (config.mockRequests) {
        let result: any = productViewMock;

        setBadgeUtil(result);
      } else {
        const itemPromise = new Promise(async (resolve, _) => {
          const frameProductUrl =
          getUrl(config, 'PRODUCT_URL') + frame.catEntryId;
          let result =  await services.getItemProperties(frameProductUrl);
          resolve(result);
        });
        itemPromise
            .then((res: any) => {
              setBadgeUtil(res.data);              
            })
            .catch(err => {
              console.error(err.status, err);
            });
      }
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setBadgeUtil = (result: any) => {
    if (!!result && !!result.CatalogEntryView && !!result.CatalogEntryView[0] && !!result.CatalogEntryView[0].Attributes) {
      let attributes = result.CatalogEntryView[0].Attributes;

      let parentId = result.CatalogEntryView[0]?.parentProductID;
       
      if(!!parentId){
        const parentPromise = new Promise(async (resolve, _) => {
          const parentProductUrl =
          getUrl(config, 'PRODUCT_URL') + parentId;
          let result =  await services.getItemProperties(parentProductUrl);
          resolve(result);
        });
        parentPromise
            .then((res: any) => {
              attributes.push(...res.data.CatalogEntryView[0].Attributes); 
              setBadge(getBadge(attributes));
            })
            .catch(err => {
              console.error(err.status, err);
            });
        }else{
          setBadge(getBadge(attributes));
        }

    }
  }

  useEffect(() => {
    const dafaultIsWarrantyActive = !!item.catentries.find((entry: WishlistCatentry) => entry.catEntryType === 'WARRANTY');
    if (dafaultIsWarrantyActive) {
      setWarrantyActive(dafaultIsWarrantyActive);
    }
  }, [item]);

  const handleWarrentyToggle = (isActive: boolean) => {
    setWarrantyActive(isActive);
    dispatch(
      editSavedItemsGlassesWarrantyAsync({
        savedItemId: item.idList.toString(),
        warrantyValue: isActive,
      })
    );
  };

  const handleDeleteItem = () => {
    dispatch(deleteItemAsync(item.idList.toString()));
  };

  const handleGoToWarrantyPolicy = (e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(config.pagesUrl.warrantyUrl, '_blank', 'noopener', 'noreferrer');
  };

  const handleAddToCart = (): void => {
    const data = {
      frameCatentryId: frame.catEntryId.toString(),
      framePartNumber: frame.partNumber,
      includeLens: item.catentries.some((item: WishlistCatentry) => item.catEntryType === 'LENS')
        ? 'true'
        : 'false',
      lens:
        item.catentries
          .find((item: WishlistCatentry) => item.catEntryType === 'LENS')
          ?.catEntryId.toString() ?? '',
      lensPartNumber:
        item.catentries.find((item: WishlistCatentry) => item.catEntryType === 'LENS')
          ?.partNumber ?? '',
      lensColor: isFrameOnly || !lensAttributesList || !lensAttributesList.LensColor ? '' : lensAttributesList.LensColor.description as string,
      orderId: '.',
      rxcReviewObject: isFrameOnly
        ? undefined
        : lensAttributesJsonString,
      tah: 'false',
      addWarranty: isWarrantyActive ? 'true' : 'false',
      warrantyCatentryId: isWarrantyActive
        ? item.catentries
            .find((item: WishlistCatentry) => item.catEntryType === 'WARRANTY')
            ?.catEntryId.toString()
        : null,
      isBundle: item.isBundle ? true : false,
      savedItemOrderId: item.catentries
      .find((item: WishlistCatentry) => item.catEntryType === 'LENS')
      ?.attributes.find((attr: Attr) => attr.name === "savedItemOrderId").value as string,
      savedItemOrderItemId: item.catentries
      .find((item: WishlistCatentry) => item.catEntryType === 'LENS')
      ?.attributes.find((attr: Attr) => attr.name === "savedItemOrderItemId").value as string
    };

    dispatch(addFrameToCartAsync({ data, savedItemId: item.idList }));
  };

  const handleAddContactToCart = (): void => {
    const rcon = item.catentries.find((item: WishlistCatentry) => item.catEntryType === 'RCON');
    const lcon = item.catentries.find((item: WishlistCatentry) => item.catEntryType === 'LCON');

    dispatch(
      addContactsToCartAsync({
        data: {
          catEntryIdLeft: lcon?.catEntryId ?? 0,
          catEntryIdRight: rcon?.catEntryId ?? 0,
          leftQuantity: lcon?.quantity.toString() ?? '',
          rightQuantity: rcon?.quantity.toString() ?? '',
          parentCatEntryId: item.catentries[0].parentCatEntryId,
          partType: 'CONTACT_LENSES',
          rxRightEye: !!(formattedItem.rightContact.cl_base_curve),
          rxLeftEye: !!(formattedItem.leftContact.cl_base_curve),
          rxRightPower:formattedItem.rightContact.cl_sphere_power,
          rxLeftPower:formattedItem.leftContact.cl_sphere_power,
          rxRightBc:formattedItem.rightContact.cl_base_curve,
          rxLeftBc:formattedItem.leftContact.cl_base_curve,
          rxRightDia:formattedItem.rightContact.cl_diameter,
          rxLeftDia:formattedItem.leftContact.cl_diameter,
          rxRightCyl:formattedItem.rightContact.cl_cylinder,
          rxLeftCyl:formattedItem.leftContact.cl_cylinder,
          rxRightAxis:formattedItem.rightContact.cl_axis,
          rxLeftAxis:formattedItem.leftContact.cl_axis,
          rxRightColor:formattedItem.rightContact.cl_color,
          rxLeftColor:formattedItem.leftContact.cl_color,
          rxRightDominance:formattedItem.rightContact.cl_dominance,
          rxLeftDominance:formattedItem.leftContact.cl_dominance,
        },
        savedItemId: item.idList,
      })
    );
  };

  const getFramePrice = (priceType: 'listPrice' | 'offerPrice') => {
    return convertNumber(frame[priceType]);
  };

  const handleFindAndTryToggle = () => {
    const params = {
      data: {
        frameCatentryId: frame.catEntryId.toString(),
        framePartNumber: frame.partNumber as string,
        frameName: frame.attributes.find(attr => attr.name === 'name')?.value as string,
        frameBrand: frame.attributes.find(attr => attr.name === 'brand')?.value as string,
        frameModel: frame.attributes.find(attr => attr.name === 'model')?.value as string,
        frameOfferPrice: convertNumber(frame.offerPrice),
        frameImageUrl: frame.attributes.find(attr => attr.name === 'imageUrl')?.value as string,
      },
      savedItemId: item.idList.toString(),
    };

    config.actions.toggleFindAndTryInStore(params);
  };

  const leftQuantity: number = formattedItem
    ? getContactDetail(formattedItem, 'left', 'quantity') === '--'
      ? 0
      : getContactDetail(formattedItem, 'left', 'quantity')
    : 0;

  const rightQuantity: number = formattedItem
    ? getContactDetail(formattedItem, 'right', 'quantity') === '--'
      ? 0
      : getContactDetail(formattedItem, 'right', 'quantity')
    : 0;

  var storePath= '/to-us/';
  if (config.storeId=='10851' && config.langId==-1) {
    storePath= '/lc-us/';
  } else if (config.storeId=='10851' && config.langId==-50) {
    storePath= '/es-us/';
  } else if (config.storeId=='10852' && config.langId==-24) {
    storePath= '/en-ca/';
  } else if (config.storeId=='10852' && config.langId==-25) {
    storePath= '/fr-ca/';
  }

  return (
    <div
      className={clsx(
        'wishlist-item-container',
        isContact && 'contact',
        itemWidth < 660 && 'mobile'
      )}
      ref={containerRef}
    >
      <div className="wishlist-item-remove-container">
        <ButtonLink
          color={config.brand === 'TO' ? 'black' : 'primary'}
          onClick={handleDeleteItem}
          icon={config.brand === 'TO' ? <TrashIcon /> : undefined}
          dataElementId="Empty_Remove"
        >
          {getLabelFallback('WISHLIST_REMOVE_ITEM')}
        </ButtonLink>
      </div>
      <div className={clsx('wishlist-item-content-container', !isInStock && 'out-of-stock')}>
        {/* IMAGE */}
        <div className="wishlist-item-image">
          {!!badge && (
            <div className="wishlist-item-content-container-badges">
                <div className="wishlist-item-content-container-badges-primary">
                  {badge.primary.map((element, index) => 
                    <div className={index > 0 ? 'space-badge' : ''}>
                      {index > 0 && (
                        <div className="space-badge-inner">|</div>
                      )}
                      {element}
                    </div>
                  )}
                </div>
                <div className="wishlist-item-content-container-badges-secondary">
                  {badge.secondary.map((element, index) => 
                    <div className={index > 0 ? 'space-badge' : ''}>
                      {index > 0 && (
                        <div className="space-badge-inner">|</div>
                      )}
                      {element}
                    </div>
                  )}
                </div>
            </div>
          )}
          <a
            class="align-center"
            data-element-id="Prod_ProdLink"
            title={
              item.catentries
                .find((item: WishlistCatentry) =>
                  atLeastOneEqual(item.catEntryType, ['SUN', 'EYE', 'RCON', 'LCON'])
                )
                .attributes.find(attr => attr.name === 'name')?.value as string
            }
            href={
              isContact
                ? config.hostname +
                  URL_EDIT_WISHLIST_CL.replace('##storeId##', config.storeId)
                    .replace(
                      '##productId##',
                      item.catentries
                        .find((item: WishlistCatentry) =>
                          atLeastOneEqual(item.catEntryType, ['RCON', 'LCON'])
                        )
                        .parentCatEntryId?.toString()
                    )
                    .replace('##langId##', config.langId.toString())
                    .replace('##catalogId##', config.catalogId)
                : config.hostname + storePath + frame.partNumber + (item.isBundle ? '?isBundle=true' : '')
                
            }
            className="cart-product-tile-image"
          >
            <Image
              width={config.brand === 'TO' ? '256px' : '304px'}
              height={config.brand === 'TO' ? '128px' : '152px'}
              src={
                item.catentries
                  .find((item: WishlistCatentry) =>
                    atLeastOneEqual(item.catEntryType, ['EYE', 'SUN', 'RCON', 'LCON'])
                  )
                  .attributes.find(attr => attr.name === 'imageUrl')?.value as string
              }
              alt={`${
                item.catentries
                  .find((item: WishlistCatentry) =>
                    atLeastOneEqual(item.catEntryType, ['EYE', 'SUN', 'RCON', 'LCON'])
                  )
                  .attributes.find(attr => attr.name === 'brand')?.value
              } ${
                item.catentries
                  .find((item: WishlistCatentry) =>
                    atLeastOneEqual(item.catEntryType, ['EYE', 'SUN', 'RCON', 'LCON'])
                  )
                  .attributes.find(attr => attr.name === 'name')?.value
              }`}
            />
          </a>
        </div>

        <div className="wishlist-item-content">
          <div className="wishlist-item-header">
            <div>
              <p className="wishlist-item-brand">
                {item.catentries
                      .find((item: WishlistCatentry) =>
                        atLeastOneEqual(item.catEntryType, ['EYE', 'SUN', 'RCON', 'LCON'])
                      )
                      ?.attributes.find(attr => attr.name === 'brand')?.value}
                {isContactLoading && <LoaderElement height={16} width={55} />}
              </p>
              <p className="wishlist-item-model-name">
                {
                  item?.catentries
                    .find((item: WishlistCatentry) =>
                      atLeastOneEqual(item.catEntryType, ['EYE', 'SUN', 'RCON', 'LCON'])
                    )
                    ?.attributes.find(attr => attr.name === 'name' && !!attr.value ? attr.name === 'name' : attr.name === 'model_name' )?.value
                }
              </p>
            </div>
            {!isContact && isInStock && (
              <div className="wishlist-item-price-container">
                {getFramePrice('listPrice') !== getFramePrice('offerPrice') && (
                  <label className="wishlist-item-price">
                    {getLabelFallback('CURRENCY_LABEL')}
                    {getFramePrice('listPrice')}
                  </label>
                )}
                <label className="wishlist-item-price-discounted">
                  {getLabelFallback('CURRENCY_LABEL')}
                  {getFramePrice('offerPrice')}
                </label>
              </div>
            )}
          </div>

          {isContact ? (
            <WishlistContactItemContent
              {...{ handleDeleteItem, isInStock, item, isContactLoading }}
            />
          ) : (
            <WishlistFrameItemContent
              {...{
                item,
                handleWarrentyToggle,
                isWarrantyActive,
                handleGoToWarrantyPolicy,
                isInStock,
              }}
            />
          )}
          {isInStock && (
            <>
              <div className="wishlist-item-subtotal-container">
                <p className="wishlist-item-subtotal-title">
                  {getLabelFallback('WISHLIST_SUBTOTAL')}
                </p>
                <div className="wishlist-item-subtotal-content">
                  {isContactLoading ? (
                    <LoaderElement height={32} width={75} />
                  ) : (
                    <>
                      {(item?.totalPercentageOff || (item?.totalFinalPrice !== item?.totalPrice)) && (
                        <div className='wishlist-item-subtotal-percentage'>
                          {getLabelFallback('PERCENTAGE_OFF_LABEL').replace('{DISCOUNT_PERCENTAGE}', item?.totalPercentageOff || ((item?.totalFinalPrice !== item?.totalPrice) &&
                              ((parseFloat(item?.totalPrice) - parseFloat(item?.totalFinalPrice)) / parseFloat(item?.totalPrice) * 100.0).toFixed(0)))}
                        </div>
                      )}
                      {item.totalFinalPrice !== item.totalPrice && (
                        <div className="wishlist-item-subtotal-price">
                          {getLabelFallback('CURRENCY_LABEL')}
                          {isContact
                            ? formattedItem &&
                              convertNumber(
                                +formattedItem.frame.xitem_basePrice *
                                  (leftQuantity + rightQuantity)
                              )
                            : convertNumber(item.totalPrice)}
                        </div>
                      )}
                      <div className="wishlist-item-subtotal-price-discounted">
                        {getLabelFallback('CURRENCY_LABEL')}
                        {isContact
                          ? formattedItem &&
                            convertNumber(
                              +formattedItem.frame.xitem_basePrice *
                                (leftQuantity + rightQuantity) +
                                -item.totalAmountOff
                            )
                          : convertNumber(item.totalFinalPrice)}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div
                className={clsx(
                  'wishlist-item-cart-container',
                  (!config.findAndTryInStoreEnabled || isContact) && 'justify-end'
                )}
              >
                {config.brand === 'LC' && config.findAndTryInStoreEnabled && !isContact && (
                  <ButtonLink
                    dataElementId="Favorites_FindInStore"
                    onClick={handleFindAndTryToggle}
                    size="m"
                    color="primary"
                  >
                    {getLabelFallback('WISHLIST_FIND_TRY_BTN')}
                  </ButtonLink>
                )}
                <button
                  className={clsx(
                    'btn btn-add-cart',
                    config.brand === 'TO' ? 'btn-outline' : 'btn-dark',
                    item.isAddingToCart && 'btn-loading'
                  )}
                  onClick={
                    !item.isAddingToCart && (isContact ? handleAddContactToCart : handleAddToCart)
                  }
                  data-element-id={config.brand === 'TO' ? 'Wishlist_Addcart' : "Favorites_AddCart"}
                  data-description={`${
                    item.catentries
                      .find(entry =>
                        atLeastOneEqual(entry.catEntryType, ['EYE', 'SUN', 'RCON', 'LCON'])
                      )
                      .attributes.find(attr => attr.name === 'model')?.value ?? ''
                  }_${
                    item.catentries.find(entry =>
                      atLeastOneEqual(entry.catEntryType, ['EYE', 'SUN', 'RCON', 'LCON'])
                    ).partNumber
                  }`}
                >
                  {item.isAddingToCart && <Loader classes="main small" />}
                  {getLabelFallback(
                    ((config.brand === 'LC' ? 'LC_' : '') + 'WISHLIST_ADD_TO_CART') as labels
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {!isInStock && (
        <div className="wishlist-item-out-of-stock">
          <p>{getLabelFallback('WISHLIST_OUT_OF_STOCK')}</p>
          {/* <button className="btn btn-dark btn-icon">
            <EnvelopIcon />
            <span>{getLabelFallback('WISHLIST_OUT_OF_STOCK_CTA')}</span>
          </button> */}
        </div>
      )}
    </div>
  );
}
